import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import api, { setTokenGetter } from "@/lib/api";

const AuthContext = createContext(null);

/**
 * AuthProvider — wraps Auth0 SDK and exposes the same shape consumed by the rest of the app.
 *
 * Wraps `useAuth0` and exposes the same shape consumed by the rest of the
 * app: { user, loading, login, logout, checkAuth }. The user object is
 * fetched from /api/auth/me (which verifies the Auth0 JWT and returns the
 * Mongo user — including is_admin, plan, is_impersonated).
 */
export const AuthProvider = ({ children }) => {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authRejected, setAuthRejected] = useState(false);

  // Register the token getter SYNCHRONOUSLY during render, not in useEffect.
  // Why: child components run their data-fetching useEffects BEFORE the
  // parent provider's useEffect, which means a getter wired up via
  // useEffect would always lose the race on first mount → 401s.
  // Setting a module-level reference during render is safe — it's
  // idempotent and not a React side-effect.
  if (isAuthenticated) {
    setTokenGetter(async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });
      return token;
    });
  } else if (!auth0Loading) {
    setTokenGetter(null);
  }

  const checkAuth = useCallback(async () => {
    if (!isAuthenticated) {
      setUser(null);
      setAuthRejected(false);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      setAuthRejected(false);
    } catch (err) {
      // Backend rejected the Auth0 token (most commonly: stale session, audience
      // mismatch, or JWKS fetch failure). Flag this so we DON'T fall back to a
      // fake user object built from the Auth0 SDK — that turns the entire
      // dashboard into a broken zombie.
      setUser(null);
      setAuthRejected(true);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (auth0Loading) return;
    checkAuth();
  }, [auth0Loading, isAuthenticated, checkAuth]);

  const login = useCallback(
    (returnTo) => {
      // Persist desired post-login destination so /callback can route there.
      if (returnTo) {
        window.sessionStorage.setItem("auth_return_to", returnTo);
      }
      return loginWithRedirect({
        appState: { returnTo: returnTo || "/dashboard" },
      });
    },
    [loginWithRedirect]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore — logout is best-effort */
    }
    setUser(null);
    auth0Logout({
      // RUNTIME origin — same rule as redirect_uri. Auth0 dashboard has every
      // environment's origin in its Allowed Logout URLs allowlist.
      logoutParams: { returnTo: window.location.origin },
    });
  }, [auth0Logout]);

  // Mirror auth0User during the brief window between Auth0 saying
  // "authenticated" and our /auth/me response landing. But if the backend
  // explicitly REJECTED the token (authRejected), don't pretend — the user
  // needs to re-login. Falling through to a fake user object leaves the
  // dashboard stuck firing API calls that all 401.
  const effectiveUser =
    user ||
    (isAuthenticated && auth0User && loading && !authRejected
      ? {
          user_id: auth0User.sub,
          email: auth0User.email,
          name: auth0User.name,
          picture: auth0User.picture,
          is_admin: false,
        }
      : null);

  return (
    <AuthContext.Provider
      value={{
        user: effectiveUser,
        setUser,
        loading: auth0Loading || loading,
        authRejected,
        checkAuth,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
